import { css } from '@emotion/core';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
    Typography
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React from 'react';
import { hasRole } from 'shared/models/user';
import { OutreachSequenceGenResultSchema } from 'tensorzero';
import { RichTextField } from '../core-ui/form-fields';

import { fullDateTime } from '../common/timestamp';
import { useCommunicationsAIData } from '../hooks/use-communications-ai-data';
import { PromptInfoData, usePromptInfo } from '../hooks/use-prompt-info';
import { useSession } from '../hooks/use-session';
import { Communication } from '../state';

const dialogStyles = css`
    .field {
        margin-bottom: 32px;
        &:last-child {
            margin-bottom: 0;
        }
    }
    .action-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
    }
    .separator {
        margin-bottom: 24px;
    }
`;

export const CommunicationAIInfo: React.FC<{ data: Communication }> = ({ data }) => {
    const { getCommunicationAIActions, getCommunicationOutreachGenInfo } = useCommunicationsAIData();
    const { userPermissions } = useSession();
    const { showPromptInfo, getPromptData } = usePromptInfo();
    const [actionsDialogOpen, setActionsDialogOpen] = React.useState(false);
    const [outreachDialogOpen, setOutreachDialogOpen] = React.useState(false);
    const [outreachPromptData, setOutreachPromptData] = React.useState<Map<string, PromptInfoData>>(new Map());

    const aiActions = getCommunicationAIActions?.(data) ?? [];
    const llmOutreaches = getCommunicationOutreachGenInfo?.(data) ?? [];

    const handleActionsClick = () => {
        setActionsDialogOpen(true);
    };

    const handleOutreachClick = () => {
        setOutreachDialogOpen(true);
        const dataMap = new Map<string, PromptInfoData>();
        Promise.all(
            llmOutreaches.map((outreach) =>
                getPromptData({ inferenceId: outreach.generationInfo?.inferenceId }).then((info) => {
                    dataMap.set(outreach.generationInfo.inferenceId, info);
                })
            )
        ).then(() => setOutreachPromptData(dataMap));
    };

    const handleActionsClose = () => {
        setActionsDialogOpen(false);
    };

    const handleOutreachClose = () => {
        setOutreachDialogOpen(false);
        setOutreachPromptData(new Map());
    };

    const handleViewPromptInfo = (generationInfo: { episodeId: string; inferenceId: string }) => () => {
        showPromptInfo(generationInfo);
    };

    const viewPromptInfo = (generationInfo: { episodeId: string; inferenceId: string }) =>
        generationInfo?.inferenceId && hasRole(userPermissions, 'prompts_editor') ? (
            <Button onClick={handleViewPromptInfo(generationInfo)}>View Prompts</Button>
        ) : null;

    let actionsDialog;
    if (actionsDialogOpen) {
        const actionFields = aiActions.map((action, index) => {
            const actionHeader =
                aiActions.length > 1 ? (
                    <div className="action-header">
                        <Typography variant="h6">Action {index + 1}</Typography>
                        {viewPromptInfo(action.generationInfo)}
                    </div>
                ) : null;

            const separator = index < aiActions.length - 1 ? <div className="separator" /> : null;

            return (
                <div key={index}>
                    {actionHeader}
                    <div className="field">
                        <TextField value={fullDateTime(action.createdAt)} label="Time" fullWidth={true} />
                    </div>
                    <div className="field">
                        <TextField value={action.action} label="Action" fullWidth={true} />
                    </div>
                    <div className="field">
                        <TextField
                            value={action.analysis.join('\n')}
                            label="Analysis"
                            fullWidth={true}
                            multiline={true}
                        />
                    </div>
                    <div className="field">
                        <TextField value={action.responseCategory} label="Response Category" fullWidth={true} />
                    </div>
                    <div className="field">
                        <TextField
                            value={action.responseText}
                            label="Response Text"
                            fullWidth={true}
                            multiline={true}
                        />
                    </div>
                    {separator}
                </div>
            );
        });

        const dialogPromptButton = aiActions.length === 1 ? viewPromptInfo(aiActions[0].generationInfo) : null;

        actionsDialog = (
            <Dialog
                open={actionsDialogOpen}
                onClose={handleActionsClose}
                maxWidth="md"
                fullWidth={true}
                css={dialogStyles}
            >
                <DialogTitle>
                    <Typography variant="h4" component="div">
                        AI Actions
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <div>{actionFields}</div>
                </DialogContent>
                <DialogActions>
                    {dialogPromptButton}
                    <Button onClick={handleActionsClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    let outreachDialog;
    if (outreachDialogOpen) {
        const outreachFields = llmOutreaches.map((outreach, index) => {
            const outreachHeader =
                llmOutreaches.length > 1 ? (
                    <div className="action-header">
                        <Typography variant="h6">Outreach {index + 1}</Typography>
                        {viewPromptInfo(outreach.generationInfo)}
                    </div>
                ) : null;

            const separator = index < llmOutreaches.length - 1 ? <div className="separator" /> : null;

            const promptData = outreachPromptData.get(outreach.generationInfo.inferenceId);
            let content;
            if (promptData) {
                const parsedResponse = promptData.parsedResponse as OutreachSequenceGenResultSchema;
                content = (
                    <>
                        <div className="field">
                            <TextField value={fullDateTime(outreach.createdAt)} label="Time" fullWidth={true} />
                        </div>
                        <div className="field">
                            <TextField value={parsedResponse.EmailSubject} label="Subject" fullWidth={true} />
                        </div>
                        <div className="field">
                            <RichTextField value={parsedResponse.FirstEmail} label="First Email" toolbar="none" />
                        </div>
                        <div className="field">
                            <RichTextField
                                value={parsedResponse.FollowUpEmail}
                                label="Follow Up Email"
                                toolbar="none"
                            />
                        </div>
                    </>
                );
            } else {
                content = <Skeleton variant="rect" height="10" />;
            }

            return (
                <div key={index}>
                    {outreachHeader}
                    {content}
                    {separator}
                </div>
            );
        });

        const dialogPromptButton = llmOutreaches.length === 1 ? viewPromptInfo(llmOutreaches[0].generationInfo) : null;

        outreachDialog = (
            <Dialog
                open={outreachDialogOpen}
                onClose={handleOutreachClose}
                maxWidth="md"
                fullWidth={true}
                css={dialogStyles}
            >
                <DialogTitle>
                    <Typography variant="h4" component="div">
                        AI Generated Outreach
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <div>{outreachFields}</div>
                </DialogContent>
                <DialogActions>
                    {dialogPromptButton}
                    <Button onClick={handleOutreachClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    if (!aiActions.length && !llmOutreaches.length) {
        return null;
    }

    const actionsButton =
        aiActions.length > 0 ? (
            <Tooltip title="AI actions taken on this message">
                <IconButton onClick={handleActionsClick}>
                    <i className="material-icons">smart_toy</i>
                </IconButton>
            </Tooltip>
        ) : null;

    const outreachButton =
        llmOutreaches.length > 0 ? (
            <Tooltip title="AI generated outreach message">
                <IconButton onClick={handleOutreachClick}>
                    <i className="material-icons">auto_awesome</i>
                </IconButton>
            </Tooltip>
        ) : null;

    return (
        <>
            {actionsButton}
            {outreachButton}
            {actionsDialog}
            {outreachDialog}
        </>
    );
};
